import type {
  GameDetailsPageDocument,
  MultiGameDetailsPageDocument,
  PremiumGameDetailsPageDocument,
} from "@/lib/cms/prismicio-types";
import type { createClient } from "@/prismic";
import type { Content } from "@prismicio/client";

import { GetSSRPropsError } from "@/types";
import { notifyError } from "../monitoring";

const getSliceFragment = (sliceType: string): string => `
...on ${sliceType} {
  variation {
    ...on default {
      primary {
        ...primaryFields
      }
    }
  }
}`;

const getSlicesFragment = (slices: string[]) => slices.map(getSliceFragment);

const getPageGraphQuery = (pageType: string, slices: string[]) => `{
  ${pageType} {
    ...${pageType}Fields
    slices {
      ${slices.join("")}
    }
  }
}`;

const getStandardGameDetailsPageGraphQuery = (pageType: string) => {
  const slices = getSlicesFragment([
    "content_box",
    "media_carousel",
    "promotion",
    "section_divider",
    // Note: Must add all supported slices here
  ]);

  slices.push(`
    ...on quest_carousel {
      variation {
        ...on default {
          primary {
            ...primaryFields
          }
        }
        ...on featureQuest {
          primary {
            ...primaryFields
            slides {
              ...slidesFields
              quest {
                ...on game_quest {
                  ...game_questFields
                }
              }
            }
          }
        }
        ...on mainQuest {
          primary {
            ...primaryFields
            slides {
              ...slidesFields
              quest {
                ...on game_quest {
                  ...game_questFields
                }
              }
            }
          }
        }
        ...on sideQuest {
          primary {
            ...primaryFields
            slides {
              ...slidesFields
              quest {
                ...on game_quest {
                  ...game_questFields
                }
              }
            }
          }
        }
      }
    }
  `);

  return getPageGraphQuery(pageType, slices);
};

const getPremiumGameDetailsPageGraphQuery = (pageType: string) => {
  const slices = getSlicesFragment([
    "content_box",
    "media_carousel",
    "any_video_embed",
    "information_section",
    // Note: Must add all supported slices here
  ]);

  slices.push(`
  ...on parallax_section {
    variation {
      ...on default {
        primary {
          ...primaryFields
          content {
            ...on parallax_section_content {
              ...parallax_section_contentFields
              slices {
                ${getSlicesFragment([
                  "promotion",
                  // Note: Must add all parallax section supported slices here
                ])}
              }
            }
          }
        }
      }
      ...on rightAlign {
        primary {
          ...primaryFields
          content {
            ...on parallax_section_content {
              ...parallax_section_contentFields
              slices {
                ${getSlicesFragment([
                  "promotion",
                  // Note: Must add all parallax section supported slices here
                ])}
              }
            }
          }
        }
      }
    }
  }`);

  return getPageGraphQuery(pageType, slices);
};

const getMultiGameDetailsPageGraphQuery = (pageType: string) => {
  const slices = getSlicesFragment([
    "information_section",
    "media_carousel",
    // Note: Must add all supported slices here
  ]);

  slices.push(`
  ...on game_parallax_section {
    variation {
      ...on default {
        primary {
          ...primaryFields
          content {
            ...on game_parallax_section_content {
              ...game_parallax_section_contentFields
              slices {
                ${getSlicesFragment([
                  "promotion",
                  // Note: Must add all parallax section supported slices here
                ])}
              }
            }
          }
        }
      }
      ...on rightAlign {
        primary {
          ...primaryFields
          content {
            ...on game_parallax_section_content {
              ...game_parallax_section_contentFields
              slices {
                ${getSlicesFragment([
                  "promotion",
                  // Note: Must add all parallax section supported slices here
                ])}
              }
            }
          }
        }
      }
    }
  }
    ...on parallax_section {
    variation {
      ...on default {
        primary {
          ...primaryFields
          content {
            ...on parallax_section_content {
              ...parallax_section_contentFields
              slices {
                ${getSlicesFragment([
                  "promotion",
                  // Note: Must add all parallax section supported slices here
                ])}
              }
            }
          }
        }
      }
      ...on rightAlign {
        primary {
          ...primaryFields
          content {
            ...on parallax_section_content {
              ...parallax_section_contentFields
              slices {
                ${getSlicesFragment([
                  "promotion",
                  // Note: Must add all parallax section supported slices here
                ])}
              }
            }
          }
        }
      }
    }
  }`);

  return getPageGraphQuery(pageType, slices);
};

export const getGamesPageAttributes = (
  templateTier: Content.GameDocumentData["template_tier"],
) => {
  switch (templateTier) {
    case "standard":
      return [
        "game_details_page",
        getStandardGameDetailsPageGraphQuery("game_details_page"),
      ] as const;

    case "multi":
      return [
        "multi_game_details_page",
        getMultiGameDetailsPageGraphQuery("multi_game_details_page"),
      ] as const;

    case "premium":
      return [
        "premium_game_details_page",
        getPremiumGameDetailsPageGraphQuery("premium_game_details_page"),
      ] as const;

    default:
      return [null, null] as const;
  }
};

export const getGamesPage = async (
  client: ReturnType<typeof createClient>,
  uid: string,
  templateTier: Content.GameDocumentData["template_tier"],
): Promise<
  | GameDetailsPageDocument
  | MultiGameDetailsPageDocument
  | PremiumGameDetailsPageDocument
  | null
> => {
  try {
    const [documentType, graphQuery] = getGamesPageAttributes(templateTier);
    if (!documentType) {
      throw new Error(GetSSRPropsError.PageNotFound);
    }

    const page = await client.getByUID(documentType, uid, { graphQuery });
    return page as
      | GameDetailsPageDocument
      | MultiGameDetailsPageDocument
      | PremiumGameDetailsPageDocument;
  } catch (error) {
    notifyError(error, "gamesPageStaticProps");
    return null;
  }
};
